import React, { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { apiPost } from '../utils/api';

const BillingSetupView = ({ email }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponValidation, setCouponValidation] = useState(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  // Base subscription price - update this to match your actual price
  const BASE_PRICE = 27.00; // $27/month
  
  // Calculate final price based on coupon
  const calculateFinalPrice = () => {
    if (!couponValidation || !couponValidation.valid || couponValidation.trialDays > 0) {
      return BASE_PRICE;
    }

    const couponDetails = couponValidation.couponDetails;
    if (!couponDetails) {
      return BASE_PRICE;
    }

    // Apply amount off discount
    if (couponDetails.amount_off) {
      const discountAmount = couponDetails.amount_off / 100; // Convert cents to dollars
      return Math.max(0, BASE_PRICE - discountAmount);
    }

    // Apply percentage off discount
    if (couponDetails.percent_off) {
      const discountAmount = (BASE_PRICE * couponDetails.percent_off) / 100;
      return Math.max(0, BASE_PRICE - discountAmount);
    }

    return BASE_PRICE;
  };

  const getDiscountAmount = () => {
    if (!couponValidation || !couponValidation.valid || couponValidation.trialDays > 0) {
      return 0;
    }

    const couponDetails = couponValidation.couponDetails;
    if (!couponDetails) {
      return 0;
    }

    if (couponDetails.amount_off) {
      return couponDetails.amount_off / 100;
    }

    if (couponDetails.percent_off) {
      return (BASE_PRICE * couponDetails.percent_off) / 100;
    }

    return 0;
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponValidation(null);
      return;
    }
    
    setIsValidatingCoupon(true);
    try {
      const response = await apiPost('/validate-coupon', { 
        couponCode: couponCode.trim() 
      });
      
      setCouponValidation({
        valid: response.valid,
        message: response.message,
        trialDays: response.trialDays,
        couponDetails: response.couponDetails
      });
    } catch (error) {
      setCouponValidation({
        valid: false,
        message: "Invalid coupon code"
      });
    } finally {
      setIsValidatingCoupon(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    if (!stripe || !elements) {
      setErrorMessage("Stripe is still loading. Please wait.");
      setIsLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setErrorMessage("Payment form is not ready. Please refresh and try again.");
      setIsLoading(false);
      return;
    }

    try {
      // Step 1: Create a payment method with Stripe
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        throw new Error(error.message);
      }

      // Step 2: Add payment method to backend
      const userId = sessionStorage.getItem('userId') || email;
      
      await apiPost('/add-payment-method', { 
        userId,
        paymentMethodId: paymentMethod.id 
      });

      console.log("✅ Payment method saved successfully!");

      // Step 3: Create subscription WITH coupon if valid
      const subscriptionResponse = await apiPost('/create-subscription', { 
        userId,
        couponCode: couponValidation?.valid ? couponCode.trim() : undefined
      });

      console.log("✅ Subscription created successfully!");

      if (subscriptionResponse.clientSecret) {
        console.log("Payment intent client secret received");
      }

      if (subscriptionResponse.trialEnd) {
        console.log(`✅ Trial active until: ${subscriptionResponse.trialEnd}`);
      }

      // Trigger event to notify App.js that billing is complete
      window.dispatchEvent(new Event('billing-setup-complete'));

      // Navigate to welcome screen
      navigate('/welcome', { replace: true });
      
    } catch (error) {
      console.error("Error setting up billing:", error);
      setErrorMessage(error.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  const finalPrice = calculateFinalPrice();
  const discountAmount = getDiscountAmount();
  const isTrial = couponValidation?.valid && couponValidation?.trialDays > 0;

  return (
    <div className="billing-setup-content max-w-md mx-auto p-8" id="billingSetupContent">
      <h2 className="billing-setup-title text-2xl font-bold mb-6 text-center" id="billingSetupTitle">Setup Billing</h2>
      <p className="billing-setup-description mb-6 text-gray-600 text-center" id="billingSetupDescription">
        Please enter your payment details to get started.
      </p>

      {/* Pricing Summary */}
      <div className="pricing-summary mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">Subscription Summary</h3>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-gray-700">Monthly Subscription</span>
            <span className={`font-medium ${discountAmount > 0 ? 'line-through text-gray-400' : 'text-gray-900'}`}>
              ${BASE_PRICE.toFixed(2)}
            </span>
          </div>

          {discountAmount > 0 && !isTrial && (
            <>
              <div className="flex justify-between items-center text-green-600">
                <span>Coupon Discount</span>
                <span className="font-medium">-${discountAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                <span className="text-lg font-semibold">Total</span>
                <span className="text-lg font-semibold text-green-600">
                  ${finalPrice.toFixed(2)}/month
                </span>
              </div>
            </>
          )}

          {isTrial && (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm text-blue-800">
                🎉 <strong>{couponValidation.trialDays}-day free trial!</strong>
                <br />
                You'll be charged ${BASE_PRICE.toFixed(2)}/month after the trial ends.
              </p>
            </div>
          )}

          {!discountAmount && !isTrial && (
            <div className="flex justify-between items-center pt-2 border-t border-gray-300">
              <span className="text-lg font-semibold">Total</span>
              <span className="text-lg font-semibold">${BASE_PRICE.toFixed(2)}/month</span>
            </div>
          )}
        </div>
      </div>

      {/* Coupon Code Section */}
      <div className="coupon-section">
        <label>Have a coupon code?</label>
        <div className="coupon-input-group">
          <input
            type="text"
            value={couponCode}
            onChange={(e) => {
              setCouponCode(e.target.value.toUpperCase());
              setCouponValidation(null); // Reset validation on change
            }}
            onBlur={validateCoupon}
            placeholder="Enter code"
            className="coupon-input"
          />
          {isValidatingCoupon && <span>Checking...</span>}
        </div>
        
        {couponValidation && (
          <div className={`coupon-message ${couponValidation.valid ? 'valid' : 'invalid'}`}>
            {couponValidation.valid ? '✅' : '❌'} {couponValidation.message}
          </div>
        )}
      </div>

      {errorMessage && (
        <div className="billing-setup-error mb-6 p-4 bg-red-100 text-red-800 rounded-lg" id="billingSetupError">
          {errorMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="billing-setup-form space-y-6" id="billingSetupForm">
        <div className="card-element-container p-4 border rounded-md bg-white" id="cardElementContainer">
          <label className="card-element-label block text-sm font-medium text-gray-700 mb-2" id="cardElementLabel">
            Card Details
          </label>
          <CardElement 
            options={cardElementOptions} 
            className="card-element" 
            id="billingCardElement"
          />
        </div>
        
        <button 
          type="submit" 
          disabled={!stripe || isLoading}
          className={`billing-setup-submit-btn w-full py-3 px-4 rounded-md text-white font-medium 
            ${(!stripe || isLoading) ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'}`}
          id="billingSetupSubmitBtn"
        >
          {isLoading ? "Processing..." : `Subscribe for ${isTrial ? 'Free Trial' : `$${finalPrice.toFixed(2)}/mo`}`}
        </button>
        
        <p className="billing-setup-disclaimer text-xs text-gray-500 text-center" id="billingSetupDisclaimer">
          Your payment information is securely processed by Stripe. We do not store your card details.
        </p>
      </form>
    </div>
  );
};

export default BillingSetupView;